import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../../../routing/LangRouter.js";
import { useForm } from "react-hook-form";

//import redux store
import { saveTicketSelectedFilters } from "../../../../store/actions/index.js";

//import constants
import { IS_MOBILE } from "../../../../constants/breakpoints.js";

//import customized hooks
import useCityList from "../../../../hooks/useCityList.js";
import useDateChange from "../../../../hooks/useDateChange.js";
import useDebouncedInputChange from "../../../../hooks/useDebouncedInputChange .js";

//import general components
import RangeDatePicker from "../../../common/range-date-picker/range-date-picker.jsx";
import PersonCount from "../../../common/person-count/person-count.jsx";
import { BaseButton } from "../../../common/base-button/base-button.jsx";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js.jsx";
import BaseRadioButton from "../../../common/base-radio-button/base-radio-button.jsx";
import BaseTextField from "../../../common/base-text-field/base-text-field.jsx";

//import images and icons
import Icon from "../../../common/base-icon/icon.jsx";
import { ArrowSwapHorizontal, CloseCircle, Location, SearchNormal } from "iconsax-react";

//import styles
import "./flight-search-form.scss";
import axios from "axios";
import BASE_URL from "../../../../constants/baseUrls.js";

function SearchFormContent({ dispatch, setRequestFlag, setUid, flightRoute, openSearchForm }) {
    //general variables
    const location = useLocation();
    const navigate = useNavigate();
    const { control } = useForm();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const destinationRef = useRef();
    const rangeDatePickerRef = useRef();
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    const [showAutocompplete, setShowAutocompplete] = useState({ departure: IS_MOBILE ? false : true, destination: IS_MOBILE ? false : true });
    const handleShowAutocompplete = name => {
        setShowAutocompplete({ [name]: true });
    };

    //handle ticket type change
    const [ticketType, setTicketType] = useState(
        isItinerary
            ? domesticFlightInformation?.departureDate !== null && domesticFlightInformation?.returnDate === null
                ? "oneWay"
                : "return"
            : searchParams.get("action") === "edit" && !!domesticFlightInformation?.returnDate
              ? "return"
              : "oneWay",
    );
    const handleRadioChange = event => {
        setTicketType(event.target.value);
        handleDateFormatChange(null);

        if (!!domesticFlightInformation) {
            domesticFlightInformation.departureDate = null;
            domesticFlightInformation.returnDate = null;
            sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));
        }
    };

    //handle destiantion city change
    const onDepartureInputChange = useDebouncedInputChange(input => {
        getDepartureCities(input);
    }, 1000);

    const onDestinationInputChange = useDebouncedInputChange(input => {
        getDestinationCities(input);
    }, 1000);

    //request city list. inputs:foreign, all, flight, accommodation, bus
    const { cities: departureCities, isCityLoading: departureLoading, getCities: getDepartureCities } = useCityList(false, false, true, false, false);
    const { cities: destinationCities, isCityLoading: destinationLoading, getCities: getDestinationCities } = useCityList(false, false, true, false, false);
    useEffect(() => {
        if (location.pathname === `/${locale}/flight-search/domestic` || location.pathname === `/${locale}/domestic-flight`) {
            getDepartureCities();
            getDestinationCities();
        }
    }, [location.pathname]);

    //handle departure and destination change
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);
    // const [departureSearchValue, setDepartureSearchValue] = useState("");
    // const [destinationSearchValue, setDestinationSearchValue] = useState("");

    const onDepartureChange = value => {
        setSelectedDeparture(value);
        if (IS_MOBILE) setShowAutocompplete({ departure: false, destination: selectedDestination || isItinerary ? false : true });
        if (!selectedDestination) destinationRef?.current?.focus();
        setUid(null);
    };

    const onDestinationChange = value => {
        setSelectedDestination(value);
        if (IS_MOBILE) setShowAutocompplete({ departure: false, destination: false });
        if (!startDate && !endDate) rangeDatePickerRef?.current?.focus();
        setUid(null);
    };

    //default value
    useEffect(() => {
        //departute & destination
        if (searchParams.get("action") === "edit") {
            setSelectedDeparture(domesticFlightInformation?.departure);
            setSelectedDestination(domesticFlightInformation?.destination);
        }
    }, [openSearchForm]);

    //handle swap change
    const handleSwapChange = () => {
        setSelectedDestination(selectedDeparture);
        setSelectedDeparture(selectedDestination);
    };

    //handle hotel date change
    const initialStartDate = domesticFlightInformation?.departureDate;
    const initialEndDate = domesticFlightInformation?.returnDate;
    const { startDate, endDate, defaultValueDate, handleDateFormatChange } = useDateChange(
        initialStartDate,
        initialEndDate,
        ticketType === "return" ? true : false,
    );
    const dateChange = (dates, _, isPersianCalendar) => {
        handleDateFormatChange(dates, isPersianCalendar);
        setUid(null);
    };

    //list of person types with their details
    const personList = [
        { id: 1, label: t("adult"), description: t("ageGroup1"), name: "adult", disabled: 1, validation: true },
        { id: 2, label: t("child"), description: t("ageGroup2"), name: "child", disabled: 0, validation: true },
        { id: 2, label: t("infant"), description: t("ageGroup3"), name: "infant", disabled: 0, validation: true },
    ];

    //handling person count change
    const [personCounter, setPersonCounter] = useState({});
    const [defaultValuePerson, setDefaultValuePerson] = useState();
    const handlePersonCountChange = ({ adult, child, infant, totalPersons }) => {
        if (totalPersons) {
            setPersonCounter({ adult, child, infant, totalPersons });
            setUid(null);
        }
    };

    //persons default value
    useEffect(() => {
        if (domesticFlightInformation)
            setDefaultValuePerson({
                adult: domesticFlightInformation.personCounter.adult ?? 1,
                child: domesticFlightInformation.personCounter.child ?? 0,
                infant: domesticFlightInformation.personCounter.infant ?? 0,
                totalPersons: domesticFlightInformation.personCounter.totalPersons ?? 1,
            });
    }, []);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //submit form
    const submitSearchForm = event => {
        event.preventDefault();
        resetFilters();

        if (selectedDeparture?.id === selectedDestination?.id) return toast.error(t("errorMessage7"), { toastId: "custom-id-yes" });
        if (!selectedDeparture || !selectedDestination || !startDate) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });
        if (ticketType === "return" && !endDate) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });

        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
        sessionStorage.removeItem("selectedFlight");
        sessionStorage.removeItem("selectedReservation");

        //set sessionStorage
        const newdomesticFlightInformation = {
            departure: selectedDeparture,
            destination: selectedDestination,
            departureDate: startDate || domesticFlightInformation?.departureDate,
            returnDate: ticketType === "return" ? endDate || domesticFlightInformation?.returnDate : null,
            personCounter: personCounter,
        };

        if (isItinerary) {
            hotelInformation.checkIn = startDate || hotelInformation?.checkIn;
            hotelInformation.checkOut = ticketType === "return" ? endDate || domesticFlightInformation?.returnDate : hotelInformation.checkOut;
            sessionStorage.setItem("hotelInformation", JSON.stringify(hotelInformation));
        }

        sessionStorage.setItem("domesticFlightInformation", JSON.stringify(newdomesticFlightInformation));
        sessionStorage.removeItem("flightPassengers");

        searchParams.delete("action");
        setSearchParams(searchParams);

        if (!flightRoute) setRequestFlag(true);
        if (flightRoute) {
            navigate(flightRoute);
        }
    };

    //list of sorting options
    const optionList = [
        { value: "oneWay", label: `${t("oneWay")}`, checked: true },
        { value: "return", label: `${t("roundTrip")}`, checked: false },
    ];

    const [priceList, setPriceList] = useState();
    const getPriceList = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}/api/v2/reserve/price_calendar/?departure=${selectedDeparture.id}&destination=${selectedDestination.id}&type=domesticFlight`,
                {
                    headers: {
                        Authorization: accessToken ? `Bearer ${accessToken}` : "",
                    },
                },
            );
            setPriceList(response.data.data);
        } catch (error) {
            console.log(error);
        } finally {
        }
    };
    useEffect(() => {
        if (!!selectedDeparture && !!selectedDestination) {
            getPriceList();
        }
    }, [selectedDeparture, selectedDestination]);

    return (
        <Grid
            component="form"
            className="flight-search-form"
            container
            item
            xs={12}
            rowGap={2.5}
            justifyContent="space-between"
            alignItems="center"
            onSubmit={submitSearchForm}
        >
            <Grid className="flight-search-form__ticket-type" container item xs={12} alignItems="center">
                <BaseRadioButton
                    name="ticketType"
                    value={ticketType}
                    optionList={optionList}
                    handleChange={handleRadioChange}
                    disabled={isItinerary ? true : false}
                />
            </Grid>

            <Grid className="flight-search-form__select" container item xs={12} md={4} justifyContent="space-between" alignItems="center">
                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("departure")}
                            value={locale === "en" ? selectedDeparture?.english_name : selectedDeparture?.name}
                            onClick={() => handleShowAutocompplete("departure")}
                            radius="10px"
                            icon={
                                <span onClick={e => e.stopPropagation()} style={{ cursor: "default", opacity: 0.5 }} tabIndex={-1}>
                                    <Icon name="DepartureIcon" width="0.8rem" />
                                </span>
                            }
                            readOnly={true}
                            disabled={isItinerary ? true : false}
                        />
                    </Grid>
                )}
                {showAutocompplete?.departure && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("from(Noun)")}
                                options={departureCities}
                                name="departure"
                                defaultValue={selectedDeparture}
                                handleChange={onDepartureChange}
                                setInputValue={onDepartureInputChange}
                                control={control}
                                loading={departureLoading}
                                disabled={isItinerary ? true : false}
                                width={350}
                                icon={<Icon name="DepartureIcon" width="0.8rem" />}
                                open={IS_MOBILE ? true : false}
                                type="other"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocompplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}

                {!isItinerary && (
                    <Box className="flight-search-form__swap">
                        <ArrowSwapHorizontal
                            size={IS_MOBILE ? "18" : "24"}
                            color="var(--light-color)"
                            onClick={selectedDeparture && selectedDestination && handleSwapChange}
                        />
                    </Box>
                )}
                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("destination")}
                            value={locale === "en" ? selectedDestination?.english_name : selectedDestination?.name}
                            onClick={() => handleShowAutocompplete("destination")}
                            radius="10px"
                            icon={
                                <span onClick={e => e.stopPropagation()} style={{ cursor: "default", opacity: 0.5 }} tabIndex={-1}>
                                    <Location size="20" color="var(--teal-color-18)" />
                                </span>
                            }
                            disabled={isItinerary}
                            readOnly={true}
                        />
                    </Grid>
                )}
                {showAutocompplete?.destination && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("to(Noun)")}
                                options={destinationCities}
                                name="destination"
                                defaultValue={selectedDestination}
                                handleChange={onDestinationChange}
                                setInputValue={onDestinationInputChange}
                                innerRef={destinationRef}
                                control={control}
                                loading={destinationLoading}
                                disabled={isItinerary ? true : false}
                                width={350}
                                icon={<Location size="20" color="var(--teal-color-18)" />}
                                open={IS_MOBILE ? true : false}
                                type="other"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocompplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container item xs={12} md={4}>
                <RangeDatePicker
                    handleChange={dateChange}
                    defaultValue={searchParams.get("action") === "edit" ? defaultValueDate : ""}
                    innerRef={rangeDatePickerRef}
                    labels={[t("departureDate"), t("returnDate")]}
                    minDate={new Date().setHours(0, 0, 0, 0)}
                    disableDate={false}
                    range={ticketType === "return" ? true : false}
                    priceList={priceList}
                />
            </Grid>

            <Grid container item xs={12} md={2} alignItems="stretch">
                <PersonCount
                    list={personList}
                    handleChange={handlePersonCountChange}
                    // menuVisibility={allPersonVisibility}
                    defaultValue={defaultValuePerson}
                />
            </Grid>

            <Grid container item md={1}>
                <BaseButton
                    text={`${t("search")}`}
                    color="primary-10"
                    size="large"
                    radius="radius-20"
                    type="submit"
                    leftIcon={<SearchNormal size="24" color="var(--primary-color)" />}
                />
            </Grid>
        </Grid>
    );
}

export default connect(mapStateToProps)(SearchFormContent);

function mapStateToProps(state) {
    return {
        ticketSort: state.ticketSort,
        ticketSelectedFilters: state.ticketSelectedFilters,
    };
}
